import React, { useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { ReadOnlyLexicalEditor } from '../../Lexical/ReadOnlyLexicalEditor/ReadOnlyLexicalEditor'
import FloatingToolbarPlugin from '../../Lexical/Plugins/FloatingToolbarPlugin/FloatingToolbarPlugin'
import { CommentPlugin } from '../../Lexical/Plugins/CommentPlugin/CommentPlugin'
import { EditorState, LexicalEditor } from 'lexical'
import { AiResult, StructuredSummaryResult } from '../../../../../../../models/AiResult'
import { FormattedResultContentType } from '../../common'
import { getResultDisplayValues } from '../utils'
import { ResultExplainerPlugin } from '../../Lexical/Plugins/ResultExplainerPlugin/ResultExplainerPlugin'
import { DefaultFloatingToolbarContent } from '../../Lexical/Plugins/FloatingToolbarPlugin/DefaultFloatingToolbarContent/DefaultFloatingToolbarContent'
import { ResultOptionsButton } from '../ResultOptions/ResultOptionsButton'
import { NotepadPlugin } from '../../Lexical/Plugins/NotepadPlugin/NotepadPlugin'
import { useDefaultResultOptions } from '../ResultOptions/DefaultResultOptions'
import { LEXICAL_NODES } from '../../Lexical/nodes'
import { AiFeature } from '../../../../../../../models/AiFeature'
import { getSafeValueOnPath } from '../../../utils'
import './TextMeetingResult.scss'

export interface TextMeetingResultProps {
  aiFeature: AiFeature
  aiResult: AiResult
  basePath: string
  path: string
  className?: string
  displayAsList?: boolean
  onFormattedResultChange: (basePath: string, path: string, contentType: string, content: string) => void
}

export const TextMeetingResult: React.FC<TextMeetingResultProps> = ({
  aiFeature,
  aiResult,
  basePath,
  path,
  onFormattedResultChange,
  className = '',
  displayAsList = false,
}) => {
  const { aiFeatureId } = aiResult
  const result: StructuredSummaryResult = getSafeValueOnPath(aiResult.result, basePath)

  const [lexicalEditor, setLexicalEditor] = useState<LexicalEditor | null>(null)

  const handleChange = useCallback(
    (editorState: EditorState) => {
      onFormattedResultChange(basePath, path, FormattedResultContentType.LEXICAL, JSON.stringify(editorState.toJSON()))
    },
    [onFormattedResultChange, basePath, path],
  )

  const { plainTextResult, htmlResult, lexicalResult } = useMemo(() => {
    return getResultDisplayValues(result, path)
  }, [result, path])

  const resultOptions = useDefaultResultOptions(lexicalEditor, aiFeature, aiResult, path, basePath)

  const TextElement = useCallback(
    () => (
      <ReadOnlyLexicalEditor
        ref={(editor) => setLexicalEditor(editor)}
        initialEditorState={lexicalResult}
        initialHtmlValue={htmlResult}
        initialTextValue={plainTextResult}
        onChange={handleChange}
        lexicalNodes={LEXICAL_NODES}
      >
        <FloatingToolbarPlugin>
          <DefaultFloatingToolbarContent aiFeature={aiFeature} aiResult={aiResult} />
        </FloatingToolbarPlugin>
        <NotepadPlugin />
        <CommentPlugin aiResult={aiResult} basePath={basePath} path={path} />
        <ResultExplainerPlugin aiFeatureId={aiFeatureId} />
      </ReadOnlyLexicalEditor>
    ),
    [aiFeature, aiFeatureId, aiResult, basePath, path, handleChange, htmlResult, lexicalResult, plainTextResult],
  )

  return (
    <div
      className={classNames(`paragraph-medium-extended d-flex text-meeting-result-container ${className}`, {
        'gap-4': displayAsList,
        'gap-2': !displayAsList,
      })}
    >
      <ResultOptionsButton resultOptions={resultOptions} />
      {displayAsList ? (
        <ul className='m-0 p-0 flex-grow-1'>
          <li>
            <TextElement />
          </li>
        </ul>
      ) : (
        <div className='flex-grow-1'>
          <TextElement />
        </div>
      )}
    </div>
  )
}
